import { useState } from 'react';

export default function TenantsLeasesFinalCTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    numberOfUnits: '',
    primaryConcern: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5qpapqhs78ip5370aq0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyAddress: '',
          numberOfUnits: '',
          primaryConcern: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-teal-500/10 via-transparent to-transparent"></div>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-teal-500/20 text-teal-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-star-line"></i>
              <span>Expert Guidance Available</span>
            </div>

            <h2 className="text-4xl font-bold text-white mb-6 leading-tight">
              Ready to Sell Your Apartment Building with Confidence?
            </h2>

            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              Managing tenants and leases during a property sale is complex. Get expert guidance to navigate the process smoothly and maximize your sale price.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-teal-400 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Legal Compliance Guaranteed</h3>
                  <p className="text-slate-400 text-sm">We ensure all tenant notifications, lease assignments, and security deposit transfers comply with state and local laws.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-teal-400 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Maximize Property Value</h3>
                  <p className="text-slate-400 text-sm">Strategic lease management and tenant retention strategies can increase your sale price by 10-20%.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-teal-400 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Faster, Smoother Closings</h3>
                  <p className="text-slate-400 text-sm">Proper documentation and tenant management prevent delays and keep your transaction on track.</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-teal-400 mb-1">500+</div>
                <div className="text-sm text-slate-400">Properties Sold</div>
              </div>
              <div className="w-px h-12 bg-slate-700"></div>
              <div className="text-center">
                <div className="text-3xl font-bold text-teal-400 mb-1">98%</div>
                <div className="text-sm text-slate-400">Client Satisfaction</div>
              </div>
              <div className="w-px h-12 bg-slate-700"></div>
              <div className="text-center">
                <div className="text-3xl font-bold text-teal-400 mb-1">$2M+</div>
                <div className="text-sm text-slate-400">Avg Sale Price</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Get Your Free Consultation</h3>
              <p className="text-slate-600">Speak with a tenant management expert about your property sale</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="tenants-leases-final-cta-form">
              <div>
                <label htmlFor="final-name" className="block text-sm font-medium text-slate-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="final-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="final-email" className="block text-sm font-medium text-slate-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="final-email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="final-phone" className="block text-sm font-medium text-slate-700 mb-1">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="final-phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="final-propertyAddress" className="block text-sm font-medium text-slate-700 mb-1">
                  Property Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="final-propertyAddress"
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="123 Main St, City, State"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="final-numberOfUnits" className="block text-sm font-medium text-slate-700 mb-1">
                    Number of Units
                  </label>
                  <input
                    type="number"
                    id="final-numberOfUnits"
                    name="numberOfUnits"
                    value={formData.numberOfUnits}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="12"
                  />
                </div>

                <div>
                  <label htmlFor="final-primaryConcern" className="block text-sm font-medium text-slate-700 mb-1">
                    Primary Concern
                  </label>
                  <select
                    id="final-primaryConcern"
                    name="primaryConcern"
                    value={formData.primaryConcern}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select</option>
                    <option value="tenant-communication">Tenant Communication</option>
                    <option value="lease-assignment">Lease Assignment</option>
                    <option value="problem-tenants">Problem Tenants</option>
                    <option value="security-deposits">Security Deposits</option>
                    <option value="legal-compliance">Legal Compliance</option>
                    <option value="timing-strategy">Timing Strategy</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="final-message" className="block text-sm font-medium text-slate-700 mb-1">
                  Additional Details
                </label>
                <textarea
                  id="final-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="Tell us about your specific situation..."
                ></textarea>
                <p className="text-xs text-slate-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting...' : 'Get Free Consultation Now'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  Thank you! A tenant management specialist will contact you within 24 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  Something went wrong. Please call us at (555) 123-4567 or try again later.
                </div>
              )}
            </form>

            <div className="mt-6 pt-6 border-t border-slate-200 text-center">
              <p className="text-sm text-slate-600 mb-3">Or call us directly:</p>
              <a
                href="tel:5551234567"
                className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-lg"
              >
                <i className="ri-phone-line"></i>
                <span>(555) 123-4567</span>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center">
          <div className="relative">
            <div className="aspect-[21/9] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/8182a25a2bad68e6adf1d8e8c5d921b1.jpg"
                alt="Successful apartment building sale with proper tenant and lease management"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
